import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { useRecaptcha } from '../../../hooks/useRecaptcha';
import { FormErrors } from '../types';
import { authApi } from '../../../api';
import { toast } from 'react-toastify';

interface ForgotPasswordModalProps {
  language: 'en' | 'sw';
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ language = 'en', onClose }) => {
  const { getToken: getRecaptchaToken, isReady: recaptchaReady } = useRecaptcha();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const content = {
    en: {
      title: 'Reset Your Password',
      description: 'Enter your email address and we will send you instructions to reset your password.',
      email: 'Email Address',
      emailPlaceholder: 'Enter your email',
      sendInstructions: 'Send Instructions',
      backToLogin: 'Back to Login',
      emailRequired: 'Email is required',
      invalidEmail: 'Please enter a valid email address',
      successTitle: 'Check Your Email',
      successMessage: 'We have sent password reset instructions to your email address.',
    },
    sw: {
      title: 'Weka Upya Nywila Yako',
      description: 'Weka barua pepe yako na tutakutumia maelekezo ya kuweka upya nywila yako.',
      email: 'Barua Pepe',
      emailPlaceholder: 'Weka barua pepe yako',
      sendInstructions: 'Tuma Maelekezo',
      backToLogin: 'Rudi Kuingia',
      emailRequired: 'Barua pepe inahitajika',
      invalidEmail: 'Tafadhali weka barua pepe sahihi',
      successTitle: 'Angalia Barua Pepe Yako',
      successMessage: 'Tumetuma maelekezo ya kuweka upya nywila kwenye barua pepe yako.',
    },
  };

  // Ensure language is valid, default to 'en' if not
  const validLanguage = (language === 'en' || language === 'sw') ? language : 'en';
  const t = content[validLanguage];

  const validateEmail = (): boolean => {
    const newErrors: FormErrors = {};

    if (!email.trim()) {
      newErrors.email = t.emailRequired;
    } else if (!/^[^\s@]+@([^\s@]+\.)+[^\s@]+$|^[^\s@]+@localhost$/.test(email)) {
      newErrors.email = t.invalidEmail;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const recaptchaToken = recaptchaReady ? await getRecaptchaToken('forgot_password') : '';
      await authApi.requestPasswordReset(email, recaptchaToken || undefined);
      setIsSuccess(true);
      toast.success(
        validLanguage === 'sw' 
          ? 'Maelekezo ya kuweka upya nywila yametumwa kwenye barua pepe yako.'
          : 'Password reset instructions have been sent to your email.',
        {
          position: 'top-center',
          autoClose: 10000,
        }
      );
    } catch (error: any) {
      const errorMessage = error?.message || error?.response?.data?.message || 
        (validLanguage === 'sw' 
          ? 'Imeshindikana kutuma maelekezo. Tafadhali jaribu tena.'
          : 'Failed to send reset instructions. Please try again.');
      setErrors({ email: errorMessage });
      toast.error(errorMessage, {
        position: 'top-center',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-card rounded-lg p-8 max-w-md w-full elevation-lg animate-scale-in text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Mail" size={32} className="text-success" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">{t.successTitle}</h3>
          <p className="text-muted-foreground mb-6">{t.successMessage}</p>
          <Button variant="default" fullWidth onClick={onClose}>
            {t.backToLogin}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-card rounded-lg p-8 max-w-md w-full elevation-lg animate-scale-in">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-foreground">{t.title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded transition-fast">
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-6">{t.description}</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="email"
            label={t.email}
            placeholder={t.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            required
          />

          <Button type="submit" variant="default" fullWidth loading={isLoading} iconName="Send" iconPosition="right">
            {t.sendInstructions}
          </Button>

          <button type="button" onClick={onClose} className="w-full text-sm text-muted-foreground hover:text-foreground font-medium transition-fast">
            {t.backToLogin}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;